import { z } from 'zod';
import { LEAVE_ACTIONS, LEAVE_ATTACHMENT_STAGES, LEAVE_STATUSES } from '@bulk/types';
import { appointmentSchema } from './appointments';
import { dateOnlySchema, idSchema, patchOf } from './common';
import { leaveSettingsSchema } from './gyms';

const localizedNames = z.record(z.string(), z.string().trim().max(80));

export const leaveTypeSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  key: z.string(),
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  deductsFromAllowance: z.boolean(),
  paid: z.boolean(),
  allowsAttachment: z.boolean(),
  requiresEligibility: z.boolean(),
  maxConsecutiveDays: z.number().int().nullable(),
  minNoticeDays: z.number().int(),
  enabledByDefault: z.boolean(),
  active: z.boolean(),
  builtin: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.string(),
});
export type LeaveTypeDto = z.infer<typeof leaveTypeSchema>;

export const createLeaveTypeSchema = z.object({
  name: z.string().trim().min(1).max(80).describe('English name; the key is made from it once and never changes'),
  nameLocalized: localizedNames.default({}),
  deductsFromAllowance: z.boolean().default(false).describe('Days taken count against the annual allowance'),
  paid: z.boolean().default(true).describe('Off means payroll deducts these days from a monthly salary'),
  allowsAttachment: z.boolean().default(false),
  requiresEligibility: z.boolean().default(false).describe('Only after the starting period (leave settings → eligibilityMonths)'),
  maxConsecutiveDays: z.number().int().min(1).max(365).nullable().default(null).describe('Working days in one request; null is unlimited'),
  minNoticeDays: z.number().int().min(0).max(365).default(0).describe('Calendar days between the request and the first day off'),
  enabledByDefault: z.boolean().default(true).describe('Off means an admin grants the type employee by employee'),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type CreateLeaveTypeInput = z.infer<typeof createLeaveTypeSchema>;
export const updateLeaveTypeSchema = patchOf(createLeaveTypeSchema);
export type UpdateLeaveTypeInput = z.infer<typeof updateLeaveTypeSchema>;

export const gymHolidaySchema = z.object({
  id: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  startDate: z.string(),
  endDate: z.string(),
  annual: z.boolean(),
  createdAt: z.string(),
});
export type GymHolidayDto = z.infer<typeof gymHolidaySchema>;

export const createGymHolidaySchema = z.object({
  name: z.string().trim().min(1).max(120),
  nameLocalized: localizedNames.default({}),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema.optional().describe('Last day, included; leave out for a single day. At most 400 days after the start, under a year when annual.'),
  annual: z.boolean().default(false).describe('Repeats on the same dates every year from the first'),
});
export type CreateGymHolidayInput = z.infer<typeof createGymHolidaySchema>;
export const updateGymHolidaySchema = patchOf(createGymHolidaySchema);
export type UpdateGymHolidayInput = z.infer<typeof updateGymHolidaySchema>;

export const dateWindowQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
});
export type DateWindowQuery = z.infer<typeof dateWindowQuerySchema>;

export const publicHolidaySchema = z.object({
  date: z.string(),
  name: z.string(),
  localName: z.string().nullable(),
  source: z.string().describe('Where this day came from: the provider (nager) or Bulk’s built-in calendar (builtin), which also fills days the provider lacks'),
});
export type PublicHolidayDto = z.infer<typeof publicHolidaySchema>;

export const holidayCalendarSchema = z.object({
  country: z.string(),
  observePublicHolidays: z.boolean(),
  from: z.string(),
  to: z.string(),
  source: z
    .object({ provider: z.string().describe('nager (Nager.Date) or builtin (Bulk’s own calendar, used when Nager.Date was unreachable); a provider year can still include built-in days it lacked'), fetchedAt: z.string() })
    .nullable(),
  publicHolidays: z.array(publicHolidaySchema),
  gymHolidays: z.array(gymHolidaySchema),
  occurrences: z.array(z.object({ gymHolidayId: idSchema, start: z.string(), end: z.string() })),
});
export type HolidayCalendarDto = z.infer<typeof holidayCalendarSchema>;

export const DAY_KINDS = ['WORKING', 'NON_WORKING_DAY', 'PUBLIC_HOLIDAY', 'GYM_HOLIDAY'] as const;

export const workingDaysSchema = z.object({
  from: z.string(),
  to: z.string(),
  workingDays: z.number().int(),
  days: z.array(z.object({ date: z.string(), kind: z.enum(DAY_KINDS) })),
});
export type WorkingDaysDto = z.infer<typeof workingDaysSchema>;

export const leaveSettingsResponseSchema = z.object({
  country: z.string(),
  settings: leaveSettingsSchema,
});
export type LeaveSettingsDto = z.infer<typeof leaveSettingsResponseSchema>;
export const updateLeaveSettingsSchema = patchOf(leaveSettingsSchema);
export type UpdateLeaveSettingsInput = z.infer<typeof updateLeaveSettingsSchema>;

export const leaveGrantSchema = z.object({
  leaveTypeId: idSchema,
  key: z.string(),
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  active: z.boolean(),
  enabledByDefault: z.boolean(),
  enabled: z.boolean(),
});
export type LeaveGrantDto = z.infer<typeof leaveGrantSchema>;

export const setLeaveGrantsSchema = z.object({
  leaveTypeIds: z.array(idSchema).max(200).describe('Every leave type this employee may request; the rest are withheld'),
});
export type SetLeaveGrantsInput = z.infer<typeof setLeaveGrantsSchema>;

export const holidaySyncResultSchema = z.object({
  queued: z.boolean(),
  country: z.string(),
});

export const leaveRuleErrorSchema = z.object({
  code: z.string(),
  message: z.string().describe('English, for logs and API clients; the web words the rule from the code and params'),
  params: z.record(z.string(), z.union([z.string(), z.number()])).describe('The values in the rule: max, days, date, left, year'),
});
export const leaveWarningSchema = z.object({
  overlapping: z.number().int(),
  limit: z.number().int(),
  colleagues: z.array(z.string()),
});
export type LeaveWarning = z.infer<typeof leaveWarningSchema>;

export const leaveAttachmentSchema = z.object({
  id: idSchema,
  stage: z.enum(LEAVE_ATTACHMENT_STAGES).describe('BEFORE_APPROVAL: the one note sent with the request, changeable until approval; AFTER_APPROVAL: added later, never changed'),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  uploadedAt: z.string(),
  uploadedByName: z.string().nullable(),
});
export type LeaveAttachmentDto = z.infer<typeof leaveAttachmentSchema>;

export const leaveAttachmentLinkSchema = z.object({
  url: z.string().describe('Signed and short-lived; fetch it right away'),
  fileName: z.string(),
  mimeType: z.string(),
});
export type LeaveAttachmentLinkDto = z.infer<typeof leaveAttachmentLinkSchema>;

export const leaveRequestSchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  employeeName: z.string(),
  departmentId: idSchema.nullable(),
  departmentName: z.string().nullable(),
  leaveTypeId: idSchema,
  leaveTypeKey: z.string(),
  leaveTypeName: z.string(),
  leaveTypeNameLocalized: z.record(z.string(), z.string()),
  paid: z.boolean(),
  deductsFromAllowance: z.boolean(),
  startDate: z.string(),
  endDate: z.string(),
  businessDays: z.number().int(),
  status: z.enum(LEAVE_STATUSES),
  reason: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  cancellationRequestedAt: z.string().nullable(),
  cancellationDecidedAt: z.string().nullable(),
  filedByName: z.string().nullable().describe('Set when someone filed it for the employee'),
  allowsAttachment: z.boolean().describe('The leave type takes a file, such as a doctor’s note'),
  attachments: z.array(leaveAttachmentSchema).describe('Oldest first'),
  attachStage: z.enum(LEAVE_ATTACHMENT_STAGES).nullable().describe('Where a file the caller uploads now would go; null when the caller may not upload one'),
  mine: z.boolean().describe("The request is for the caller's own employee record"),
  canDecide: z.boolean().describe('The caller may approve, reject or decide a cancellation of this request (nobody but the owner decides their own leave)'),
  warning: leaveWarningSchema.nullable().describe('More people of the department off at once than it allows (advisory)'),
  createdAt: z.string(),
});
export type LeaveRequestDto = z.infer<typeof leaveRequestSchema>;

export const leaveRequestEventSchema = z.object({
  id: idSchema,
  fromStatus: z.enum(LEAVE_STATUSES).nullable(),
  toStatus: z.enum(LEAVE_STATUSES),
  actorName: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type LeaveRequestEventDto = z.infer<typeof leaveRequestEventSchema>;

export const createLeaveRequestSchema = z.object({
  employeeId: idSchema.optional().describe('Leave for someone else, filed by an approver; leave out for your own'),
  leaveTypeId: idSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  reason: z.string().trim().max(500).nullable().optional(),
  approve: z.boolean().optional().describe("An approver filing for someone else records it approved at once"),
});
export type CreateLeaveRequestInput = z.infer<typeof createLeaveRequestSchema>;

export const updateLeaveRequestSchema = z.object({
  startDate: dateOnlySchema.optional(),
  endDate: dateOnlySchema.optional(),
  reason: z.string().trim().max(500).nullable().optional(),
});
export type UpdateLeaveRequestInput = z.infer<typeof updateLeaveRequestSchema>;

export const leaveActionSchema = z.object({
  action: z.enum(LEAVE_ACTIONS),
  note: z.string().trim().max(500).nullable().optional().describe('A rejection reason, a decision note or why the leave should be cancelled'),
  cancelSessions: z.boolean().optional().describe("On approve: cancel the one-to-one sessions booked with this person on the leave's days, telling each member and giving package sessions back. Left out, they stay booked"),
});
export type LeaveActionInput = z.infer<typeof leaveActionSchema>;

export const leaveSessionsSchema = z.object({
  total: z.number().int().describe("One-to-one sessions still booked with this person on the leave's days, from now on"),
  sessions: z.array(appointmentSchema).nullable().describe('The sessions themselves; null when the caller may not read appointments, so an approver without that right sees only the count'),
});
export type LeaveSessionsDto = z.infer<typeof leaveSessionsSchema>;

export const leavePreviewInputSchema = createLeaveRequestSchema.extend({
  requestId: idSchema.optional().describe('The request being edited, left out of its own overlap and allowance'),
});
export type LeavePreviewInput = z.infer<typeof leavePreviewInputSchema>;

export const leavePreviewSchema = z.object({
  businessDays: z.number().int(),
  eligibleFrom: z.string(),
  skipped: z.array(z.object({ date: z.string(), kind: z.enum(['PUBLIC_HOLIDAY', 'GYM_HOLIDAY']) })),
  allowance: z.object({ total: z.number().int(), usedBefore: z.number().int(), after: z.number().int() }).nullable(),
  errors: z.array(leaveRuleErrorSchema),
  warning: leaveWarningSchema.nullable(),
});
export type LeavePreviewDto = z.infer<typeof leavePreviewSchema>;

export const leavePersonSchema = z.object({
  id: idSchema,
  firstName: z.string(),
  lastName: z.string(),
  position: z.string().nullable(),
  departmentName: z.string().nullable(),
});
export type LeavePersonDto = z.infer<typeof leavePersonSchema>;

export const LEAVE_LIST_SCOPES = ['mine', 'all', 'approvals'] as const;
export const leaveRequestListQuerySchema = z.object({
  scope: z.enum(LEAVE_LIST_SCOPES).default('mine'),
  status: z.enum(LEAVE_STATUSES).optional(),
  leaveTypeId: idSchema.optional(),
  departmentId: idSchema.optional(),
  employeeId: idSchema.optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  q: z.string().trim().max(80).optional(),
});
export type LeaveRequestListQuery = z.infer<typeof leaveRequestListQuerySchema>;

export const leaveBalanceSchema = z.object({
  employeeId: idSchema,
  year: z.number().int(),
  allowance: z.number().int(),
  used: z.number().int().describe('Approved (or awaiting a cancellation decision) days of types that use the allowance'),
  pending: z.number().int(),
  remaining: z.number().int(),
  eligibleFrom: z.string(),
  eligible: z.boolean(),
  resetsOn: z.string(),
  byType: z.array(
    z.object({
      leaveTypeId: idSchema,
      key: z.string(),
      name: z.string(),
      nameLocalized: z.record(z.string(), z.string()),
      paid: z.boolean(),
      deductsFromAllowance: z.boolean(),
      approvedDays: z.number().int(),
      pendingDays: z.number().int(),
    }),
  ),
});
export type LeaveBalanceDto = z.infer<typeof leaveBalanceSchema>;

export const leaveBalanceQuerySchema = z.object({
  employeeId: idSchema.optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});

export const leaveCalendarQuerySchema = dateWindowQuerySchema.extend({ departmentId: idSchema.optional() });
export type LeaveCalendarQuery = z.infer<typeof leaveCalendarQuerySchema>;

export const leaveCalendarSchema = z.object({
  from: z.string(),
  to: z.string(),
  country: z.string().describe("The gym's country; public holidays carry their name in its language as localName"),
  workDays: z.array(z.string()),
  publicHolidays: z.array(publicHolidaySchema),
  gymHolidays: z.array(z.object({ gymHolidayId: idSchema, name: z.string(), nameLocalized: z.record(z.string(), z.string()), start: z.string(), end: z.string() })),
  entries: z.array(
    z.object({
      requestId: idSchema,
      employeeId: idSchema,
      employeeName: z.string(),
      mine: z.boolean().describe("The leave is the caller's own"),
      departmentId: idSchema.nullable(),
      departmentName: z.string().nullable(),
      typeHidden: z.boolean().describe('The caller sees only that the person is off: the type goes to the person, whoever may decide their leave and leave.read'),
      leaveTypeId: idSchema.nullable(),
      leaveTypeKey: z.string().nullable(),
      leaveTypeName: z.string().nullable(),
      leaveTypeNameLocalized: z.record(z.string(), z.string()).nullable(),
      startDate: z.string(),
      endDate: z.string(),
      status: z.enum(LEAVE_STATUSES),
      businessDays: z.number().int(),
      reason: z.string().nullable().describe('Only for people who can see everyone’s leave'),
    }),
  ),
});
export type LeaveCalendarDto = z.infer<typeof leaveCalendarSchema>;
